import React from 'react';
import { PortableText as SanityPortableText } from '@portabletext/react';
import CardCode from './CardCode';
import { CARD_CODE_REGEX } from '../utils/cardCodes';

/**
 * Takes a plain string and replaces every card-code match with a <CardCode> component.
 * Returns an array of strings and React elements.
 */
function processTextForCardCodes(text) {
  if (typeof text !== 'string') return text;

  const parts = [];
  let lastIndex = 0;
  let match;

  // Reset regex state
  CARD_CODE_REGEX.lastIndex = 0;

  while ((match = CARD_CODE_REGEX.exec(text)) !== null) {
    // Push preceding plain text
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    // Push interactive CardCode
    parts.push(<CardCode key={`cc-${match.index}`} cardNumber={match[1]} />);
    lastIndex = CARD_CODE_REGEX.lastIndex;
  }

  // Remaining plain text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
}

// ─── Custom serializer components for Portable Text ───────────
const portableTextComponents = {
  block: {
    h1: ({ children }) => (
      <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-4 text-foreground">
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-3 text-foreground">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="text-xl md:text-2xl font-semibold mt-6 mb-2 text-foreground">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="text-lg font-semibold mt-4 mb-2 text-foreground">
        {children}
      </h4>
    ),
    normal: ({ children }) => (
      <p className="mb-4 leading-relaxed text-foreground/90">{children}</p>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-primary/40 pl-4 italic my-4 text-muted-foreground">
        {children}
      </blockquote>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="list-disc list-inside mb-4 space-y-1 text-foreground/90">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="list-decimal list-inside mb-4 space-y-1 text-foreground/90">
        {children}
      </ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <li className="leading-relaxed">{children}</li>,
    number: ({ children }) => <li className="leading-relaxed">{children}</li>,
  },
  marks: {
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em>{children}</em>,
    code: ({ children }) => (
      <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">
        {children}
      </code>
    ),
    link: ({ children, value }) => {
      const href = value?.href || '';
      // Card-link scheme: [Display Name](card:BP05-044) → interactive card
      const cardMatch = href.match(/^card:(.+)$/);
      if (cardMatch) {
        return <CardCode cardNumber={cardMatch[1]}>{children}</CardCode>;
      }
      return (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline underline-offset-2 hover:text-primary/80"
        >
          {children}
        </a>
      );
    },
    // Card reference annotation (from Sanity Studio "Card reference" toolbar)
    cardCode: ({ children, value }) => (
      <CardCode cardNumber={value?.cardNumber}>{children}</CardCode>
    ),
  },
  types: {
    image: ({ value }) => {
      if (!value?.asset) return null;
      return (
        <figure className="my-6">
          <img
            src={value.asset.url || value.asset._ref}
            alt={value.alt || ''}
            className="rounded-lg w-full"
            loading="lazy"
          />
          {value.caption && (
            <figcaption className="text-sm text-muted-foreground mt-2 text-center">
              {value.caption}
            </figcaption>
          )}
        </figure>
      );
    },
  },
};

/**
 * Wrapper around @portabletext/react that auto-detects card codes
 * in span text and replaces them with interactive <CardCode> components.
 *
 * Usage:
 *   <BlogPortableText value={post.content} />
 */
export default function BlogPortableText({ value }) {
  if (!value) return null;

  // Pre-process the Portable Text blocks to inject card code detection
  // into span children so that plain-text card codes become interactive.
  const processedValue = value.map((block) => {
    if (block._type !== 'block') return block;
    return block;
  });

  return (
    <SanityPortableText
      value={processedValue}
      components={{
        ...portableTextComponents,
        // Override the default span rendering to auto-detect card codes
        block: {
          ...portableTextComponents.block,
          normal: ({ children }) => (
            <p className="mb-4 leading-relaxed text-foreground/90">
              {processChildren(children)}
            </p>
          ),
          h1: ({ children }) => (
            <h1 className="text-3xl md:text-4xl font-bold mt-8 mb-4 text-foreground">
              {children}
            </h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-2xl md:text-3xl font-bold mt-8 mb-3 text-foreground">
              {children}
            </h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-xl md:text-2xl font-semibold mt-6 mb-2 text-foreground">
              {children}
            </h3>
          ),
          h4: ({ children }) => (
            <h4 className="text-lg font-semibold mt-4 mb-2 text-foreground">
              {children}
            </h4>
          ),
          blockquote: portableTextComponents.block.blockquote,
        },
        marks: portableTextComponents.marks,
        list: portableTextComponents.list,
        listItem: portableTextComponents.listItem,
        types: portableTextComponents.types,
      }}
    />
  );
}

/**
 * Walk through React children and replace any string children that
 * contain card-code patterns with mixed text/CardCode elements.
 */
function processChildren(children) {
  if (!children) return children;
  if (!Array.isArray(children)) {
    return typeof children === 'string'
      ? processTextForCardCodes(children)
      : children;
  }
  return children.map((child, i) => {
    if (typeof child === 'string') {
      const processed = processTextForCardCodes(child);
      // If processing produced an array, wrap with a fragment
      return Array.isArray(processed) ? (
        <React.Fragment key={i}>{processed}</React.Fragment>
      ) : (
        processed
      );
    }
    return child;
  });
}
